/**
 * The employee's notification bell — the one place leave decisions surface.
 *
 * Why the bell rather than cards on My Leave (where these notices first
 * shipped): the chrome is visible from BOTH employee screens, so an employee
 * sitting on My Pay finds out that their leave was decided. Cards on My Leave
 * only reached someone who had already navigated to the screen the news was
 * about. It is also one owner for the concept — `docs/design-system/CLAUDE.md`
 * § "One canonical component per status/badge concept" — instead of the same
 * decision rendering in two places that drift.
 *
 * What it is NOT: a push notification. Nothing arrives while the app is
 * closed, the phone does not buzz, and the app icon carries no badge. The
 * count here updates when the app is opened or brought back to the
 * foreground. Real push needs FCM/APNs credentials and a device-token store.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useBreakpoint,
} from '@jumpstone/ui-react';
import {
  useEmployeeNotifications,
  type EmployeeNotification,
} from '../../data/use_employee_notifications.js';
import {
  dismiss_device_notification,
  useDeviceNotifications,
} from '../../data/device_notifications.js';
import { format_date, format_relative_time } from '../../lib/format.js';
import { open_downloaded_file } from '../../lib/native_file_open.js';
import { navigate } from '../../lib/router.js';
import { BellRow, BellRowIcon } from './bell_row.js';
import { DeviceNoticeRow } from './employee_device_notice_row.js';
import {
  merge_bell_items,
  type BellItem,
} from './employee_notification_items.js';
import { useEssNotificationsControl } from './ess_notifications.js';
import { BellIcon, CloseIcon, EssPhoneBellIcon } from './bell_icons.js';

/**
 * Tailwind's `lg` (1024px). Below it the panel is a bottom sheet; from it up,
 * a dropdown under the bell. Same switch as the bell's own `lg:hidden` glyph
 * and `.wp-ess-header`'s phone plane (styles/wagepro-theme.css), so the
 * sheet and the phone app bar always appear together.
 */
const SHEET_BELOW_WIDTH = 1024;

/**
 * `format_date` on an absent or malformed value throws
 * (`new Date('')` -> Invalid Date -> `Intl.DateTimeFormat.formatToParts`
 * raises `RangeError: date value is not finite`). `template_vars` is parsed
 * from a TEXT column and the server deliberately degrades a malformed blob to
 * `{}`, so "no date" is a reachable state — and this bell renders in the
 * chrome of EVERY employee screen, with no ErrorBoundary anywhere in
 * wagepro-web. One bad row would therefore white-screen the whole app, and
 * the employee could not even dismiss it, because dismiss lives inside the
 * panel that crashed.
 *
 * Renders an empty string instead. A notice that reads a little bare is a
 * far better outcome than an app that will not open.
 */
/**
 * The leave-type codes that have catalog entries.
 *
 * Leave types are employer-configurable (`wp.settings.leave_types.*`), but
 * only these five are translated. `t()` has no `defaultValue` support — on a
 * miss `useTranslation` returns the literal key — so translating an
 * employer-defined code like "study" unconditionally would render
 * "my_leave.leave_type.study" to the employee inside the sentence. Anything
 * outside this set is passed through as the employer typed it, which is worse
 * only than a translation that does not exist.
 */
const TRANSLATED_LEAVE_TYPES: ReadonlySet<string> = new Set([
  'annual',
  'sick',
  'casual',
  'unpaid',
  'other',
]);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function safe_date(value: string | undefined, locale: string): string {
  if (value === undefined || !ISO_DATE.test(value)) return '';
  return format_date(value, locale);
}

/** Same guard for the timestamp: `Intl.RelativeTimeFormat` on an unparseable
 *  `created_at` produces `NaN` units and throws. */
function safe_relative_time(at: string, now: string, locale: string): string {
  if (Number.isNaN(new Date(at).getTime())) return '';
  return format_relative_time(at, now, locale);
}

export function EmployeeNotificationBell({
  open_file = open_downloaded_file,
}: {
  /** Opens a downloaded file; injectable so tests can see the tap land. */
  open_file?: (uri: string, mime_type: string) => void;
} = {}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { notifications, unread_count, dismiss } = useEmployeeNotifications();
  // On-device notices (a downloaded payslip) share the list and the count
  // with the server's leave decisions — one bell, not two.
  const device_notifications = useDeviceNotifications();
  const items = merge_bell_items(notifications, device_notifications);
  const badge_count = unread_count + device_notifications.length;
  // One timestamp for the whole panel: every row's "2 hours ago" is measured
  // from the same instant, so two notices a minute apart cannot render as the
  // same relative time through re-render drift.
  const now = new Date().toISOString();
  // Controlled on the employee surface, so the Profile screen's
  // "Notifications" row can open this same panel (`ess_notifications.tsx`);
  // local state anywhere without the provider. Controlled either way, so the
  // panel's own close control (and opening a notice) can shut it.
  const control = useEssNotificationsControl();
  const [local_open, set_local_open] = useState(false);
  const open = control ? control.open : local_open;
  const set_open = control ? control.set_open : set_local_open;
  const close = (): void => {
    set_open(false);
  };
  // Phone/tablet: a bottom sheet, not a dropdown. A dropdown anchored under
  // the bell is nearly full-width on a phone and always lands on the page's
  // own title, right under the app bar (WagePro bug-bash 2026-10-15, #7).
  const as_sheet = useBreakpoint().width < SHEET_BELOW_WIDTH;
  // The sheet has no `onCloseAutoFocus`, so every sheet close consumes the
  // Profile row's hand-off itself; otherwise a stale opener would be
  // refocused by some later, unrelated close.
  const close_sheet = (): void => {
    control?.take_return_focus();
    set_open(false);
  };
  // Dismissing the sheet (its close button, the backdrop, Escape) hands
  // focus back to the Profile row when that is what opened it. Explicitly,
  // not via the Drawer's own restore: WebKit does not focus a tapped
  // <button>, so in the iOS shell the Drawer captured <body> as its opener.
  // Next frame, so this lands after the Drawer's own restore has run.
  const dismiss_sheet = (): void => {
    const opener = control?.take_return_focus() ?? null;
    set_open(false);
    if (opener !== null) {
      requestAnimationFrame(() => {
        opener.focus();
      });
    }
  };
  const on_done = as_sheet ? close_sheet : close;

  const bell_glyphs = (
    <>
      {/* Classic bell on desktop, the phone design's bell below `lg`. */}
      <BellIcon className="hidden lg:block" />
      <EssPhoneBellIcon className="lg:hidden" />
      {badge_count > 0 && (
        /* The count, not a bare dot: "2 waiting" is worth knowing before
           you tap. Hidden entirely at zero so the chrome is quiet when there
           is nothing to say. `ring` separates it from the bell glyph
           underneath so it stays legible against the glass bar, and the cap
           keeps a three-digit number from stretching the pill across the
           icon. */
        <span
          className="wp-ess-bell-count absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--color-danger)] text-white text-[10.5px] font-bold leading-[18px] text-center ring-2 ring-white num"
          data-testid="employee-notification-badge"
        >
          {badge_count > 99 ? '99+' : badge_count}
        </span>
      )}
      {/* The phone app bar shows the design's plain unread dot in place
          of the count pill (`.wp-ess-bell-dot`, hidden everywhere else).
          The count is still in the button's accessible name. */}
      {badge_count > 0 && (
        <span
          className="wp-ess-bell-dot"
          aria-hidden="true"
          data-testid="employee-notification-dot"
        />
      )}
    </>
  );
  const bell_label = t('notification.open', { count: badge_count });

  // ONE list and ONE exit for both containers; only the chrome around them
  // differs, so the sheet and the dropdown cannot drift apart.
  const list = (
    <NotificationList
      items={items}
      now={now}
      locale={locale}
      t={t}
      dismiss={dismiss}
      open_file={open_file}
      on_done={on_done}
    />
  );
  const view_all = (
    <ViewAllButton label={t('my_leave.title')} on_done={on_done} />
  );

  if (as_sheet) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          className="wp-ess-bell relative"
          aria-label={bell_label}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => {
            set_open(!open);
          }}
          data-testid="employee-notification-bell"
        >
          {bell_glyphs}
        </Button>
        {/* The Drawer brings the title, its own close button, the backdrop
            (tap outside closes), Escape and the focus trap. Focus opens on
            the close button (`initialFocus="close"`), so a stray Enter
            cannot open, and so dismiss, the first notice. The close button
            is named from WagePro's own catalog: the Drawer's default comes
            from a `common` namespace this app does not ship. */}
        <Drawer
          side="bottom"
          open={open}
          onClose={dismiss_sheet}
          title={t('notification.heading')}
          closeLabel={t('common.close')}
          initialFocus="close"
          footer={view_all}
        >
          {/* Rows carry their own `px-3`; pull them out to the sheet's edge
              so their text lines up with the title. */}
          <div className="-mx-3" data-testid="employee-notification-sheet">
            {list}
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={set_open}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="wp-ess-bell relative"
          aria-label={bell_label}
          data-testid="employee-notification-bell"
        >
          {bell_glyphs}
        </Button>
      </DropdownMenuTrigger>

      {/*
       * Desktop: a dropdown anchored under the bell, not a panel that takes
       * the screen. `DropdownMenuContent` already brings the rounded corner,
       * the white surface, the shadow, its own vertical padding and a height
       * cap from Radix's available-space variable — so this adds a width and
       * nothing else. An earlier version overrode all four (`p-0`,
       * `max-h-[70vh]`), which is what made it read as a big box.
       *
       * The LIST scrolls, not the whole panel, so the heading and the exit
       * stay put while the notices move under them.
       */}
      <DropdownMenuContent
        align="end"
        className="w-[320px] max-w-[calc(100vw-1.5rem)]"
        data-testid="employee-notification-panel"
        onCloseAutoFocus={(event) => {
          // Opened from the Profile row? Hand focus back there, not to the
          // bell in the app bar (see `EssNotificationsControl.open_from`).
          const opener = control?.take_return_focus() ?? null;
          if (opener === null) return;
          event.preventDefault();
          opener.focus();
        }}
      >
        <div className="flex items-center justify-between gap-2 pe-1">
          <DropdownMenuLabel>{t('notification.heading')}</DropdownMenuLabel>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.close')}
            onClick={close}
            data-testid="employee-notification-close"
          >
            <CloseIcon />
          </Button>
        </div>
        <DropdownMenuSeparator />

        <div className="max-h-[320px] overflow-y-auto">{list}</div>
        {/* A way out that is not "tap a notice". */}
        <DropdownMenuSeparator />
        {view_all}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The notices (or the empty state), shared by the sheet and the dropdown. */
function NotificationList({
  items,
  now,
  locale,
  t,
  dismiss,
  open_file,
  on_done,
}: {
  items: BellItem[];
  now: string;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  dismiss: (notification_id: string) => Promise<void>;
  open_file: (uri: string, mime_type: string) => void;
  /** Closes the panel once a notice has been acted on. */
  on_done: () => void;
}): React.JSX.Element {
  if (items.length === 0) {
    return <EmptyPanel label={t('notification.empty')} />;
  }
  return (
    <>
      {items.map(({ kind, notice }) =>
        kind === 'device' ? (
          <DeviceNoticeRow
            key={notice.id}
            notice={notice}
            relative_time={safe_relative_time(notice.created_at, now, locale)}
            onOpen={() => {
              // Same rule as a leave notice: opening it clears it.
              dismiss_device_notification(notice.id);
              open_file(notice.uri, notice.mime_type);
              on_done();
            }}
          />
        ) : (
          <NoticeRow
            key={notice.id}
            notice={notice}
            locale={locale}
            now={now}
            t={t}
            onOpen={() => {
              // Reading it IS dismissing it: the decision stays on the
              // request in My Leave's history, so keeping the notice around
              // after the employee has been taken to it would be asking them
              // to tidy up twice.
              void dismiss(notice.id);
              navigate({ module: 'my_leave' });
              on_done();
            }}
          />
        ),
      )}
    </>
  );
}

/**
 * The panel's exit to My Leave. Reuses My Leave's own title: the destination
 * names itself, and it costs no new string.
 */
function ViewAllButton({
  label,
  on_done,
}: {
  label: string;
  on_done: () => void;
}): React.JSX.Element {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        navigate({ module: 'my_leave' });
        on_done();
      }}
      className="w-full justify-center text-[12.5px] font-semibold text-[color:var(--accent)]"
      data-testid="employee-notification-view-all"
    >
      {label}
    </Button>
  );
}

function EmptyPanel({ label }: { label: string }): React.JSX.Element {
  return (
    <div className="px-3 py-6 text-center">
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--accent-tint)] text-[color:var(--accent)]">
        <BellIcon />
      </span>
      <p className="mt-2 text-[12.5px] text-[color:var(--ink-muted)]">
        {label}
      </p>
    </div>
  );
}

/**
 * Approved gets a tick; declined gets a minus, not a cross. A cross reads as
 * "something went wrong" — a declined request is a decision, not a fault, and
 * the employee did nothing incorrectly by asking.
 */
function DecisionIcon({ approved }: { approved: boolean }): React.JSX.Element {
  return (
    <BellRowIcon
      className={
        approved
          ? 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)]'
          : 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)]'
      }
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {approved ? (
          <polyline points="20 6 9 17 4 12" />
        ) : (
          <line x1="5" y1="12" x2="19" y2="12" />
        )}
      </svg>
    </BellRowIcon>
  );
}

function NoticeRow({
  notice,
  locale,
  now,
  t,
  onOpen,
}: {
  notice: EmployeeNotification;
  locale: string;
  now: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  onOpen: () => void;
}): React.JSX.Element {
  const approved = notice.type === 'leave_request_approved';
  const reason = notice.template_vars.rejection_reason;
  const leave_type = notice.template_vars.leave_type ?? '';
  return (
    <BellRow
      icon={<DecisionIcon approved={approved} />}
      title={
        approved
          ? t('my_leave.notice.approved_title')
          : t('my_leave.notice.rejected_title')
      }
      time={safe_relative_time(notice.created_at, now, locale)}
      onOpen={onOpen}
      testId="employee-notification-item"
    >
      {/* Clamped to two lines so a long employer name cannot make one
            notice three times the height of its neighbour. */}
      <span className="block text-[12.5px] text-[color:var(--ink-muted)] mt-0.5 line-clamp-2">
        {t('my_leave.notice.body', {
          employer: notice.template_vars.employer_name ?? '',
          leave_type: TRANSLATED_LEAVE_TYPES.has(leave_type)
            ? t(`my_leave.leave_type.${leave_type}`)
            : leave_type,
          start_date: safe_date(notice.template_vars.start_date, locale),
          end_date: safe_date(notice.template_vars.end_date, locale),
        })}
      </span>

      {reason !== undefined && reason !== '' && (
        /* Set off by a rule rather than run on as another muted line: this
             is the employer's own words, and it is the one thing an employee
             opens a decline to read. */
        <span className="block mt-1.5 ps-2 border-s-2 border-[color:var(--color-danger)] text-[12.5px] text-[color:var(--ink)]">
          {reason}
        </span>
      )}
    </BellRow>
  );
}
